import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Table, FilterValues, PageHeader, Card, Button, showSnackbar } from "@cofano/cck";
import { HtmlEvent } from "@cofano/cck/Table/Table";
import CargoFilter, { convertFilterToJSON } from "components/cargo/CargoFilter";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import useLayout from "components/layout/useLayout";
import CargosDownload from "view/cargos/components/download/CargosDownload";
import { TableId, useBackendTable } from "components/table";
import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import VoyageSelect, { VoyageSelectOption } from "components/selects/VoyageSelect";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";
import DeliveryConditionSelect from "view/deliveryconditions/components/selects/DeliveryConditionSelect";
import BulkStatusChanger from "view/statuses/components/changer/BulkStatusChanger";
import CommentsWidget from "components/comments/CommentsWidget";
import useComments from "components/comments/useComments";
import { Language, DataFormat, Fields, usePermission, hasPluginPermission, hasOptionalFieldPermission } from "imports/Utils";

import CargoSideviewer from "./components/sideviewer/CargoSideviewer";

import CargoImportDialog from "view/cargos/components/dialogs/CargoImportDialog";

import { DossierLink, LocationLink, ProductLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import { cargos_query, deleteCargo_mutation } from "./utils/CargoQueries";

import { StatusCargoEntity } from "types/index";
import { ChildPropsFromPagination } from "types/commons";
import { Cargos_CargosQuery } from "__generated__/graphql";
import {
	CharterSubtype,
	CommentEntityType,
	RelationType,
	StatusType,
} from "__generated__/enums";

import { ProductSelectOption } from "view/products/components/select/ProductSelect";
import { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import SalesCharterStatusChanger from "view/charters/components/status/SalesCharterStatusChanger";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import SalesCharterStatusSelect, {
	SalesCharterStatusSelectOption,
} from "view/charters/components/selects/SalesCharterStatusSelect";
import { AddLink, ChatBubbleOutline, ViewSidebar } from "@mui/icons-material";
import { ShipLinkDialog } from "view/charters/dossier/ship/ShipLinkDialog";
import CargoServiceIcons from "view/cargos/components/CargoServiceIcons";
import { StatusSelectOption } from "view/statuses/components/selects/StatusSelect";
import CargoQuantity from "view/cargos/components/quantities/CargoQuantity";
import { SortDirection, SalesCharterStatus } from "__generated__/enums";
import ConnectingVisitField from "view/cargos/components/connectingVisits/ConnectingVisitField";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";

type Cargo = ChildPropsFromPagination<Cargos_CargosQuery, "cargos">

const Cargos = () => {

	const confirm = useConfirm();

	const layout = useLayout();
	const session = useStoreState(state => state.session);
	const client = useApolloClient();
	const navigate = useNavigate();

	const { isRTR, isOperator, isCustomer, isFinance, isReadOnly } = usePermission();

	const salesCharterPageAsCargoDetails = (isRTR || isCustomer) && hasPluginPermission(session, "salesCharterPageAsCargoDetails");

	const [ cargoImportOpen, setCargoImportOpen ] = useState(false);

	const { tableProps, filterProps, filterValuesProps, refetch, refetchAfterDelete, filters, clearSelection } = useBackendTable({
		id: TableId.Cargos,
		selectSettings: {
			isFullSelectEnabled: false,
		},
		onChange: async(pagination, sort, filter, callback) => {
			const cargoSearch = convertFilterToJSON(filter) as Record<string, unknown>;
			delete cargoSearch.charterSubtype;
			delete cargoSearch.charterStatus;
			delete cargoSearch.brokers;

			try {
				const result = await client.query({
					query: cargos_query,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "created",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							...cargoSearch,
							id: filter.id ? (Number.isInteger(filter.id) ? filter.id : 0) : undefined,
							charterSearch: {
								charterSubtypes: filter.charterSubtype,
								statuses: filter.charterStatus?.map((option: SalesCharterStatusSelectOption) => option.name) ?? [],
								brokers: filter.brokers?.map((option: RelationSelectOption) => option.id),
							},
						},
					},
				});

				callback({
					content: result.data.cargos.content,
					totalResults: result.data.cargos.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const { commentsProps, openComment } = useComments({
		cargos: tableProps.content as Cargo[],
		calls: (tableProps.content as Cargo[]).flatMap(c => c.actions).map(a => a.call).filter(a => !!a).flat(),
		onChange: () => refetch(),
	});

	const [ deleteCargo ] = useMutation(deleteCargo_mutation);

	const onCargoRowClick = useCallback((id: number) => {
		layout.setDrawerSettings({
			open: true,
			title: intl.formatMessage({ id: "CARGO" }),
			content: (
				<CargoSideviewer
					cargoId={id}
					onChange={refetch} />
			),
			size: "normal",
			variant: "overlay",
		});
	}, []);

	enum RedirectTarget {
		SalesCharter,
		CargoEdit,
		CargoDetail
	}

	const navigateToCargo = useCallback((row: Cargo, event: HtmlEvent, redirectTarget: RedirectTarget) =>  {
		const { id, charter } = row;
		const charterId = charter?.id ?? 0;
		const controlPressed = event.ctrlKey;

		let url;

		const salesCharterUrl = `/view/salescharters/${charterId}/details`;
		const cargoEditUrl = salesCharterPageAsCargoDetails ? `/view/salescharters/${charterId}/details?edit-cargo=${id}` : `/view/cargos/${id}/edit`;
		const cargoDetailsUrl = `/view/cargos/${id}/details`;

		switch (redirectTarget) {
			case RedirectTarget.CargoDetail:
				url = cargoDetailsUrl;
				break;
			case RedirectTarget.CargoEdit:
				url = cargoEditUrl;
				break;
			case RedirectTarget.SalesCharter:
				url = salesCharterUrl;
				break;
			default:
				url = cargoDetailsUrl;
				break;
		}

		if (controlPressed) {
			window.open(url);
		} else {
			navigate(url);
		}
	}, []);

	const [ linkShipToCargo, setLinkShipToCargo ] = useState<Cargo | undefined>(undefined);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("CARGOS")}
					<Helmet title={intl.formatMessage({ id: "CARGOS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && isRTR && (
						<Button onClick={() => setCargoImportOpen(true)} variant="outlined">
							{Language("IMPORT")}
						</Button>
					)}

					{!isReadOnly && !hasPluginPermission(session, "externalCargosManagement") && isRTR && (
						<Button onClick={() => navigate("/view/salescharters/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<div style={{ marginBottom: 16 }}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "id",
								title: "ID",
							}, {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
							}, {
								id: "relations",
								title: intl.formatMessage({ id: "ORDER_CUSTOMER" }),
								renderFilterValue: (value) => value?.map((item: RelationSelectOption) => item.name).join(", "),
							}, {
								id: "products",
								title: intl.formatMessage({ id: "PRODUCT" }),
								renderFilterValue: (value) => value?.map((item: ProductSelectOption) => item.name).join(", "),
							}, {
								id: "productDetails",
								title: intl.formatMessage({ id: "PRODUCT_DETAILS" }),
							}, {
								id: "ships",
								title: intl.formatMessage({ id: "VOYAGE_SHIPS" }),
								renderFilterValue: (value) => value?.map((item: ShipSelectOption) => item.name).join(", "),
							}, {
								id: "voyages",
								title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
								renderFilterValue: (value) => value?.map((item: VoyageSelectOption) => item.voyageNumber).join(", "),
							}, {
								id: "currentStatuses",
								title: intl.formatMessage({ id: "STATUS" }),
								renderFilterValue: (value) => value?.map((item: StatusSelectOption) => item.description).join(", "),
							}, {
								id: "hasDangerousGoods",
								title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
								renderFilterValue: (value) => DataFormat.booleanAsText(value),
							}, {
								id: "loadingLocations",
								title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
								renderFilterValue: (value) => value?.map((item: LocationSelectOption) => item.description).join(", "),
							}, {
								id: "dischargingLocations",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
								renderFilterValue: (value) => value?.map((item: LocationSelectOption) => item.description).join(", "),
							}, {
								id: "loadingWindow",
								title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
								renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
							}, {
								id: "dischargingWindow",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
								renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
							}, {
								id: "searchLabel",
								title: intl.formatMessage({ id: "ORDER_SEARCH_LABEL" }),
							}, {
								id: "viaLoadingLocation",
								title: intl.formatMessage({ id: "CARGOS_VIA_LOCATION_LOADING" }),
								renderFilterValue: (value) => value?.description,
							}, {
								id: "viaDischargingLocation",
								title: intl.formatMessage({ id: "CARGOS_VIA_LOCATION_DISCHARGING" }),
								renderFilterValue: (value) => value?.description,
							}, {
								id: "billOfLadingNumber",
								title: intl.formatMessage({ id: "BL_NUMBER" }),
							}, {
								id: "bookingReference",
								title: intl.formatMessage({ id: "CARGO_BOOKING_REFERENCE" }),
							}, {
								id: "loadingReference",
								title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
							}, {
								id: "dischargingReference",
								title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
							}, {
								id: "created",
								title: intl.formatMessage({ id: "ORDER_CREATED" }),
								renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
							}, {
								id: "isPlanned",
								title: intl.formatMessage({ id: "CARGO_PLANNED" }),
								renderFilterValue: (value) => DataFormat.booleanAsText(value),
							}, {
								id: "deliveryCondition",
								title: intl.formatMessage({ id: "DELIVERY_CONDITION" }),
								renderFilterValue: (value) => value.description,
							}, {
								id: "charterStatus",
								title: intl.formatMessage({ id: "CHARTER_ORDER_STATUS" }),
								renderFilterValue: (value) => value.map((item: SalesCharterStatusSelectOption) => intl.formatMessage({ id: item.name })).join(", "),
							}, {
								id: "charterSubtype",
								title: intl.formatMessage({ id: "CHARTER_TYPE" }),
								renderFilterValue: (value) => value.map((type: CharterSubtype) => intl.formatMessage({ id: type })).join(", "),
							}, {
								id: "brokers",
								title: intl.formatMessage({ id: "BROKER" }),
								renderFilterValue: (value) => value.map((broker: RelationSelectOption) => broker.name).join(", "),
							}  ]}
						/>
					</Card>
				</div>
			)}
			<Card disablePadding>
				<Table
					{...tableProps}

					onRowDoubleClick={(row, e) => navigateToCargo(row as Cargo, e, salesCharterPageAsCargoDetails ? RedirectTarget.SalesCharter : RedirectTarget.CargoDetail)}
					filterComponent={() => <CargoFilter {...filterProps} newBreakPoints={undefined} />}
					columns={[ {
						id: "id",
						title: intl.formatMessage({ id: "CARGO_ID" }),
						sortable: true,
						defaultVisible: false,
						filterComponent: "NUMBER",
					}, {
						id: "cargoNumber",
						title: intl.formatMessage({ id: "CARGO_NUMBER" }),
						width: 150,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "cargoNumber"),
					}, {
						id: "charterNumber",
						title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						render: (row: Cargo) => <DossierLink charter={row.charter} />,
						sortable: true,
						sortKey: "charter.charterNumber",
						disabled: !isRTR,
						width: 150,
					},
					{
						id: "charterStatus",
						title: intl.formatMessage({ id: "CHARTER_ORDER_STATUS" }),
						render: (row: Cargo) => row.charter && (
							<SalesCharterStatusChanger charter={row.charter} onCompleted={refetch} />
						),
						disabled: !isRTR,
						width: 180,
						filterComponent: (value, onChange) => (
							<SalesCharterStatusSelect
								label={intl.formatMessage({ id: "CHARTER_ORDER_STATUS" })}
								value={value}
								multiple
								onChange={onChange}
							/>
						),
					},
					{
						id: "charterSubtype",
						title: intl.formatMessage({ id: "CHARTER_SUBTYPE" }),
						render: (row: Cargo) => <CharterSubtypeTag type={row.charter?.subtype} />,
						disabled: !isRTR,
						width: 150,
						filterComponent: (value, onChange) => (
							<CharterSubtypeSelect
								value={value}
								label={intl.formatMessage({ id: "CHARTER_TYPE" })}
								onChange={options => onChange(options)}
								multiple
							/>
						),
					},
					{
						id: "lastStatusChange",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row: Cargo) => {
							return (
								<StatusChanger
									session={session}
									entity={row as StatusCargoEntity}
									statusType={StatusType.Cargo}
									refetch={refetch}
									showHistory
								/>
							);
						},
						sortable: true,
						sortKey: "lastStatusChange.status.description",
						disabled: !hasPluginPermission(session, "cargoStatuses"),
						width: 175,
					}, {
						id: "relation",
						title: intl.formatMessage({ id: "ORDER_CUSTOMER" }),
						render: (row: Cargo) => <RelationLink relation={row.charter?.debtor} />,
						sortable: true,
						sortKey: "charter.debtor.name",
						disabled: !(hasOptionalFieldPermission(session, Fields.ORDER, "relation")),
					},
					{
						id: "brokers",
						title: intl.formatMessage({ id: "BROKER" }),
						sortable: true,
						sortKey: "charter.broker.name",
						width: 200,
						render: (row) => <RelationLink relation={row.charter?.broker} />,
						filterComponent: (value, onChange) => (
							<RelationSelect
								types={[ RelationType.Broker ]}
								label={intl.formatMessage({ id: "BROKER" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					},
					{
						id: "product",
						title: intl.formatMessage({ id: "PRODUCT" }),
						render: (row: Cargo) => <ProductLink product={row.product} />,
						sortable: true,
						sortKey: "product.name",
						disabled: !hasPluginPermission(session, "products"),
					}, {
						id: "weight",
						title: intl.formatMessage({ id: "WEIGHT" }),
						render: (row: Cargo) => DataFormat.tonnage(row.weight),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "weight"),
					}, {
						id: "volume",
						title: intl.formatMessage({ id: "CARGO_VOLUME" }),
						render: (row: Cargo) => DataFormat.volumeM3(row.volume),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "volume"),
					}, {
						id: "teu",
						title: intl.formatMessage({ id: "TEU" }),
						render: (row: Cargo) =>  DataFormat.simpleUnit(row.teu, DataFormat.UNITS.TEU),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "teu"),
						defaultVisible: false,
					}, {
						id: "length",
						title: `${intl.formatMessage({ id: "SHIPS_DIMENSIONS" })}(cm)`,
						render: (row: Cargo) =>  row.dimensions && `${row.dimensions.length || "-"} x ${row.dimensions.width || "-"} x ${row.dimensions.height || "-"}cm`,
						defaultVisible: false,
						width: 200,
					}, {
						id: "ships",
						title: intl.formatMessage({ id: "SHIP" }),
						render: (row: Cargo) => <ShipLink ship={row.firstLoadingAction?.call?.voyage.ship} />,
						filterComponent: isRTR || isCustomer || isOperator || isFinance
							? (value, onChange) => (
								<ShipSelect
									label={intl.formatMessage({ id: "SHIP" })}
									value={value}
									onChange={(options) => onChange(options)}
									multiple />
							) : undefined,
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.ship.name",
						disabled: !hasPluginPermission(session, "voyages"),
						width: 200,
					}, {
						id: "voyages",
						title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
						render: (row: Cargo) => <VoyageLink voyage={row.firstLoadingAction?.call?.voyage} />,
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.voyageNumber",
						width: 200,
						filterComponent: (value, onChange) => (
							<VoyageSelect
								label={intl.formatMessage({ id: "VOYAGE_NUMBER" })}
								value={value}
								includeExpired={true}
								onChange={(options) => onChange(options)}
								multiple />
						),
						disabled: !hasPluginPermission(session, "voyages"),
					}, {
						id: "firstLoadingAction.location",
						title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
						render: (row: Cargo) => <LocationLink location={row.firstLoadingAction?.location} />,
						sortable: true,
						sortKey: "firstLoadingAction.location.description",
					}, {
						id: "firstLoadingAction.start",
						title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
						render: (row: Cargo) => row.firstLoadingAction && <CargoActionDate action={row.firstLoadingAction} />,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingDate"),
						sortKey: "firstLoadingAction.start",
					}, {
						id: "lastDischargingAction.location",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
						render: (row: Cargo) => <LocationLink location={row.lastDischargingAction?.location} />,
						sortable: true,
						sortKey: "lastDischargingAction.location.description",
					}, {
						id: "lastDischargingAction.start",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
						render: (row: Cargo) => row.lastDischargingAction && <CargoActionDate action={row.lastDischargingAction} />,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingDate"),
						sortKey: "lastDischargingAction.start",
					}, {
						id: "actionsWindow",
						title: intl.formatMessage({ id: "START_END_WINDOW" }),
						render: (row: Cargo) => <FormattedDateWindow from={row.firstLoadingAction?.start} to={row.lastDischargingAction?.end} withoutYear />,
						sortable: true,
						sortKey: "firstLoadingAction.start",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingWindow"),
						width: 250,
					}, {
						id: "searchLabel",
						title: intl.formatMessage({ id: "ORDER_SEARCH_LABEL" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "searchLabel"),
						defaultVisible: false,
					}, {
						id: "isPlanned",
						title: intl.formatMessage({ id: "CARGO_PLANNED" }),
						render: (row: Cargo) => DataFormat.booleanAsText(!!row.firstLoadingAction?.call?.voyage),
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.voyageNumber",
						disabled: !hasPluginPermission(session, "voyages"),
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.viaLocation",
						title: intl.formatMessage({ id: "CARGOS_VIA_LOCATION_LOADING" }),
						render: (row: Cargo) => <LocationLink location={row.firstLoadingAction?.viaLocation} />,
						sortable: true,
						sortKey: "firstLoadingAction.viaLocation.description",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "viaLocation"),
						defaultVisible: false,
						width: 150,
					}, {
						id: "lastDischargingAction.viaLocation",
						title: intl.formatMessage({ id: "CARGOS_VIA_LOCATION_DISCHARGING" }),
						render: (row: Cargo) => <LocationLink location={row.lastDischargingAction?.viaLocation} />,
						sortable: true,
						sortKey: "lastDischargingAction.viaLocation.description",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "viaLocation"),
						defaultVisible: false,
						width: 150,
					}, {
						id: "billOfLadingNumber",
						title: intl.formatMessage({ id: "BL_NUMBER" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "billOfLading"),
						defaultVisible: false,
					}, {
						id: "bookingReference",
						title: intl.formatMessage({ id: "CARGO_BOOKING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "bookingReference"),
						defaultVisible: false,
					}, {
						id: "product.adnAttributes.unNumber",
						title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
						render: (row: Cargo) => DataFormat.booleanAsText(row.hasDangerousGoods),
						defaultVisible: false,
					}, {
						id: "productDetails",
						title: intl.formatMessage({ id: "PRODUCT_DETAILS" }),
						sortable: true,
						defaultVisible: false,
						width: 150,
					}, {
						id: "firstLoadingAction.windowFrom",
						title: intl.formatMessage({ id: "ORDER_LOADING_WINDOW" }),
						render: (row: Cargo) => <FormattedDateWindow from={row.firstLoadingAction?.start} to={row.firstLoadingAction?.end} withoutYear />,
						width: 250,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingWindow"),
						defaultVisible: false,
					}, {
						id: "loadingReference",
						title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingReference"),
						defaultVisible: false,
					}, {
						id: "lastDischargingAction.windowFrom",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_WINDOW" }),
						render: (row: Cargo) => <FormattedDateWindow from={row.lastDischargingAction?.start} to={row.lastDischargingAction?.end} withoutYear />,
						width: 250,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingWindow"),
						defaultVisible: false,
					}, {
						id: "dischargingReference",
						title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingReference"),
						defaultVisible: false,
					}, {
						id: "revenue",
						title: intl.formatMessage({ id: "REVENUE" }),
						render: (row: Cargo) => DataFormat.price(row.revenue),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "revenue"),
						defaultVisible: true,
					}, {
						id: "deliveryCondition",
						title: intl.formatMessage({ id: "DELIVERY_CONDITION" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "deliveryCondition"),
						defaultVisible: false,
						sortKey: "deliveryCondition.description",
						render: (row: Cargo) => row.deliveryCondition?.description || "",
						filterComponent: (value, onChange) => (
							<DeliveryConditionSelect
								label={intl.formatMessage({ id: "DELIVERY_CONDITION" })}
								value={value}
								onChange={(option) => onChange(option)} />
						),
					}, {
						id: "color",
						title: intl.formatMessage({ id: "COLOR" }),
						render: (row: Cargo) => DataFormat.color(row.color),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "cargoColor"),
						defaultVisible: false,
					}, {
						id: "created",
						title: intl.formatMessage({ id: "ORDER_CREATED" }),
						render: (row: Cargo) => <FormattedDate date={row.created} withoutYear />,
						sortable: true,
						defaultVisible: false,
					}, {
						id: "charter.internalNotes",
						title: intl.formatMessage({ id: "CARGO_CHARTER_INTERNAL_NOTES" }),
						defaultVisible: false,
					}, {
						id: "services",
						title: intl.formatMessage({ id: "CARGO_SERVICES" }),
						render: (row: Cargo) => <CargoServiceIcons cargo={row} />,
						width: 100,
						disableFlexGrow: true,
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.connectingShipPortVisit.ship",
						title: intl.formatMessage({ id: "LOADING_CONNECTING_SHIP" }),
						render: (row: Cargo) => <ConnectingVisitField action={row.firstLoadingAction} />,
						width: 150,
						defaultVisible: true,
					},  {
						id: "lastDischargingAction.connectingShipPortVisit.ship",
						title: intl.formatMessage({ id: "DISCHARGING_CONNECTING_SHIP" }),
						render: (row: Cargo) => <ConnectingVisitField action={row.lastDischargingAction} />,
						width: 150,
						defaultVisible: true,
					}, {
						id: "quantity",
						title: intl.formatMessage({ id: "QUANTITY" }),
						render: (row: Cargo) => (
							<CargoQuantity cargo={row} />
						),
					}, {
						id: "charter.firstLoadingDate",
						title: intl.formatMessage({ id: "CARGO_CHARTER_FIRST_PLANNED_LOADING_DATE" }),
						sortable: true,
						sortKey: "charter.firstLoadingDate",
						render: (row: Cargo) => <FormattedDate date={row.charter?.firstLoadingDate} format="ll" />,
						defaultVisible: false,
					} ]}
					actions={[ {
						render: (selection, isFullSelect, _event, state) =>
							<CargosDownload
								selection={selection || []}
								isFullSelect={isFullSelect}
								filters={filters}
								columns={state.columnSettings.columns}
								totalResults={tableProps.paginationSettings.totalResults}
								onDownload={() => clearSelection()} />,
					}, {
						render: (selectedRows) =>
							<BulkStatusChanger
								statusType={StatusType.Cargo}
								entities={selectedRows || []}
								refetch={refetch}
							/>,
					} ]}
					rowActions={isReadOnly ? undefined : [
						{
							icon: <ViewSidebar />,
							tooltip: intl.formatMessage({ id: "VIEW_SIDEBAR" }),
							onClick: (row) => onCargoRowClick(row.id),
							disabled: (row) => row.id == null,
						}, {
							icon: <AddLink />,
							tooltip: intl.formatMessage({ id: "ADD_SHIPS" }),
							onClick: setLinkShipToCargo,
							hidden:  !(isRTR) || hasPluginPermission(session, "externalCargosManagement"),
							disabled: (row) => !row.charter || row.firstLoadingAction?.call?.voyage.ship || row.charter.status === SalesCharterStatus.Cancelled,
						}, {
							icon: <ChevronRightIcon />,
							onClick: (row, event) => navigateToCargo(row, event, RedirectTarget.CargoDetail),
							hidden: !(isRTR || isCustomer) || hasPluginPermission(session, "externalCargosManagement") || salesCharterPageAsCargoDetails,
						}, {
							icon: <EditIcon />,
							onClick: (row, event) => navigateToCargo(row, event, RedirectTarget.CargoEdit),
							hidden: !(isRTR) || hasPluginPermission(session, "externalCargosManagement"),
						},
						{
							icon: <ChatBubbleOutline  fontSize="small" />,
							disabled: () => false,
							onClick: (row: Cargo) => openComment(row.id, CommentEntityType.Cargo),
						}, {
							icon: <DeleteIcon />,
							onClick: (row: Cargo) => {
								confirm({
									type: "DELETE",
								}).then(() =>
									deleteCargo({
										variables: {
											id: row.id,
											deleteEmptyVoyage: true,
										},
									}).then(() => refetchAfterDelete()),
								);
							},
							hidden: !(isRTR || isOperator) || hasPluginPermission(session, "externalCargosManagement"),
						},
					]}
				/>
			</Card>

			<CargoImportDialog open={cargoImportOpen} onClose={setCargoImportOpen} />

			{linkShipToCargo?.charter && (
				<ShipLinkDialog
					open={!!linkShipToCargo}
					onClose={() => setLinkShipToCargo(undefined)}
					charter={linkShipToCargo.charter}
					onCompleted={(voyage) => {
						showSnackbar(
							`${intl.formatMessage({ id: "PLANNED_CARGO_ON" })} ${voyage.voyageNumber}`,
							{ variant: "success" },
						);
						refetch();
					}}
					resetShipNomination
				/>
			)}

			<CommentsWidget {...commentsProps} />
		</div>
	);
};

export default Cargos;
